import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { LazyMount } from "@/components/LazyMount";
import { Footer } from "@/components/Footer";
import { UNIVERS, universBySlug } from "@/data/univers";

import { ThermometreMental } from "@/components/ThermometreMental";
import { CuriositeSection } from "@/components/CuriositeSection";
import { ChambreNoire } from "@/components/ChambreNoire";
import { ScribeSection } from "@/components/ScribeSection";
import { Temoin } from "@/components/Temoin";
import { Aiguillon } from "@/components/Aiguillon";
import { Terrier } from "@/components/Terrier";
import { BoussoleIntro } from "@/components/BoussoleIntro";
import { TreasureHunt } from "@/components/TreasureHunt";
import { LaboHub } from "@/components/LaboHub";
import { CuriosityHub } from "@/components/CuriosityHub";
import { Devineur } from "@/components/Devineur";
import { VraiOuBluff } from "@/components/VraiOuBluff";
import { JeuxLogique } from "@/components/JeuxLogique";
import { LexiquesHub } from "@/components/LexiquesHub";
import { CapsulesHub } from "@/components/CapsulesHub";
import { AcademieCard } from "@/components/AcademieCard";
import { Conference } from "@/components/Conference";
import { FeuDeCampSection } from "@/components/FeuDeCampSection";
import { Arene } from "@/components/Arene";
import { AreneEvolution } from "@/components/AreneEvolution";

// Body is the scroll container on this app (html/body height:100% + overflow-x:hidden),
// which makes element.scrollIntoView({behavior:'smooth'}) unreliable inside mobile
// in-app browsers. Scroll the real scroller explicitly instead, and re-run after
// lazy content mounts so the anchor lands accurately under the sticky sub-nav.
const HEADER_OFFSET = 128;
const getScroller = () => {
  for (const el of [document.scrollingElement, document.documentElement, document.body]) {
    if (el && el.scrollHeight > el.clientHeight + 4) return el;
  }
  return document.body;
};
const scrollToAnchor = (id) => {
  if (!id) { getScroller().scrollTo({ top: 0, behavior: "auto" }); window.scrollTo(0, 0); return; }
  const doScroll = () => {
    const el = document.getElementById(id);
    if (!el) return;
    const scroller = getScroller();
    const target = el.getBoundingClientRect().top + scroller.scrollTop - HEADER_OFFSET;
    scroller.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  };
  doScroll();
  setTimeout(doScroll, 300);
  setTimeout(doScroll, 650);
};

// Which tool sections render for each anchor. Heavy hubs are lazy-mounted.
const SECTIONS = {
  "thermometre-mental": () => <ThermometreMental />,
  "la-curiosite": () => <CuriositeSection />,
  "chambre-noire": () => <ChambreNoire />,
  "le-scribe": () => <ScribeSection />,
  "le-temoin": () => <Temoin />,
  "l-aiguillon": () => <Aiguillon />,
  "terrier-lapin": () => <Terrier />,
  "boussole": () => <BoussoleIntro />,
  "chasse-tresor": () => <LazyMount minHeight={320} testId="lazy-treasure"><TreasureHunt /></LazyMount>,
  "le-labo": () => <LaboHub />,
  "le-devineur": () => <Devineur />,
  "vrai-bluff": () => <VraiOuBluff />,
  "jeux-logique": () => <JeuxLogique />,
  "les-lexiques": () => <LazyMount minHeight={640} testId="lazy-lexiques"><LexiquesHub /></LazyMount>,
  "les-capsules": () => <LazyMount minHeight={640} testId="lazy-capsules"><CapsulesHub /></LazyMount>,
  "l-academie": () => <AcademieCard />,
  "les-conferences": () => <Conference />,
  "les-debats": () => <><Arene /><AreneEvolution /></>,
  "feu-de-camp": () => <FeuDeCampSection />,
};

// Exploration also gets the Curiosity hub (not in the submenu, kept accessible here).
const EXTRA = {
  "l-exploration": [{ anchor: "curiosity-hub", render: () => <LazyMount minHeight={640} testId="lazy-curiosity"><CuriosityHub /></LazyMount> }],
};

export default function Univers() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const u = universBySlug(slug);

  // Deep-link: scroll to the requested tool anchor (content lazy-loads, so retry).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) { scrollToAnchor(null); return; }
    const id = hash.slice(1);
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) { scrollToAnchor(id); return; }
      if (tries++ < 14) setTimeout(tick, 300);
    };
    setTimeout(tick, 350);
  }, [slug]);

  if (!u || !u.slug) return <Navigate to="/" replace />;

  const sections = [
    ...u.tools.filter((tool) => SECTIONS[tool.anchor]).map((tool) => ({ anchor: tool.anchor, name: tool.name, render: SECTIONS[tool.anchor] })),
    ...(EXTRA[slug] || []).map((e) => ({ ...e, name: null })),
  ];
  const others = UNIVERS.filter((o) => o.id !== u.id);

  const scrollTo = (anchor) => scrollToAnchor(anchor);

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip" data-testid={`univers-page-${u.id}`}>
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-12%", left: "50%", transform: "translateX(-50%)", width: "820px", height: "620px" }} />

      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="univers-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/" className="hidden sm:inline text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.home", "Accueil")}</Link>
            <Link to="/tarifs" className="hidden sm:inline text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.tarifs")}</Link>
            <Link to="/entrer" data-testid="univers-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      {/* Univers hero header */}
      <section className="relative min-h-[46vh] flex flex-col justify-center items-center text-center px-6 pt-32 pb-14">
        <div aria-hidden className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(circle at 50% 40%, hsl(${u.hue} 90% 55% / 0.18), transparent 60%)` }} />
        <Link to="/" className="relative z-10 inline-flex items-center gap-2 text-sm text-foreground/50 hover:text-primary transition-colors mb-6" data-testid="univers-back">
          <ArrowLeft size={15} /> {t("nav.home", "Accueil")}
        </Link>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="relative z-10 tracking-[0.3em] uppercase text-xs mb-4" style={{ color: `hsl(${u.hue} 80% 62%)` }}>{t("landing.zones." + u.id + ".eyebrow", "Univers")}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
          className="relative z-10 font-display text-gradient-orange text-5xl sm:text-6xl lg:text-7xl leading-[0.98] pb-[0.06em]" data-testid="univers-heading">{u.label}</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.25 }}
          className="relative z-10 mt-5 font-light text-foreground/65 text-lg max-w-2xl">{u.promise[lang] || u.promise.fr}</motion.p>
      </section>

      {/* Sticky sub-nav of tools */}
      <nav className="sticky top-16 z-40 backdrop-blur-xl bg-background/70 border-y border-white/10" data-testid="univers-subnav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-2 overflow-x-auto no-scrollbar py-3">
          {u.tools.filter((tool) => SECTIONS[tool.anchor]).map((tool) => (
            <button key={tool.anchor} onClick={() => scrollTo(tool.anchor)}
              data-testid={`univers-nav-${tool.anchor}`}
              className="whitespace-nowrap text-sm px-3.5 py-1.5 rounded-full border border-white/10 text-foreground/60 hover:text-primary hover:border-primary/40 transition-colors">
              {tool.name}
            </button>
          ))}
        </div>
      </nav>

      {/* Tool sections stacked */}
      <div className="relative z-10">
        {sections.map((s, i) => (
          <section key={s.anchor} id={s.anchor} style={{ scrollMarginTop: "128px" }} className={i > 0 ? "pt-16 sm:pt-24" : "pt-10"} data-testid={`univers-section-${s.anchor}`}>
            {s.render()}
          </section>
        ))}
      </div>

      {/* Cross-links to the other univers */}
      <section className="max-w-6xl mx-auto px-6 py-24" data-testid="univers-crosslinks">
        <h2 className="font-display text-gradient-orange text-2xl sm:text-3xl text-center mb-10">{t("landing.zones." + u.id + ".title", "Continue l'exploration")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {others.map((o) => {
            const to = o.slug ? `/univers/${o.slug}` : (o.tools[0]?.to || "/");
            return (
              <Link key={o.id} to={to} data-testid={`univers-crosslink-${o.id}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 glass p-5 hover:border-primary/40 transition-colors">
                <div aria-hidden className="absolute -top-10 -right-8 w-32 h-32 rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle, hsl(${o.hue} 90% 55% / 0.2), transparent 65%)` }} />
                <span className="relative z-10 font-display text-lg text-foreground/90 group-hover:text-primary transition-colors flex items-center gap-1.5">
                  {o.label} <ArrowUpRight size={15} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
